import React from 'react';
import { TableCell, TableSortLabel } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { SortOrder } from '@models/table.model.ts';
import { CSS } from '@dnd-kit/utilities';
import TableColumnFilter from '@components/table-column-filter/table-column-filter.tsx';
import TableSearchIcon from '@components/dynamic-table/components/table-search-icon/table-search-icon.tsx';
import TableDateRangeFilter from '@components/dynamic-table/components/table-date-range-filter/table-date-range-filter.tsx';
import { useTranslation } from 'react-i18next';

interface SortableHeaderCellProps {
  column: any;
  index: number;
  columnsLength: number;
  sort: [string, SortOrder];
  onSort: (columnId: string) => void;
  onSearch: (columnId: string, value: string) => void;
  onFilter: (columnId: string, value: string[]) => void;
  onDateRangeFilter: (
    columnId: string,
    value: [Date | null, Date | null]
  ) => void;
  search: Record<string, string>;
  filters: Record<string, string[]>;
  betweenFilters: Record<string, { from: string; to: string }>;
  clearSearch?: (columnId: string) => void;
  clearFilters?: (columnId: string) => void;
  clearDateRangeFilters?: (columnId: string) => void;
  language?: string;
}

export const SortableHeaderCell: React.FC<SortableHeaderCellProps> = ({
  column,
  index,
  columnsLength,
  sort,
  onSort,
  onSearch,
  onFilter,
  onDateRangeFilter,
  search,
  filters,
  betweenFilters,
  clearSearch,
  clearFilters,
  clearDateRangeFilters,
  language
}) => {
  const { t } = useTranslation();
  const isLast = index === columnsLength - 1;
  const disabledDrag = column.disabledDrag ?? isLast;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: column.id,
    disabled: disabledDrag
  });

  const style: React.CSSProperties = {
    transform:
      !isLast && transform
        ? `${CSS.Transform.toString(transform)} scale(1)`
        : undefined,
    transition: !isLast ? transition : undefined,
    cursor: disabledDrag ? 'default' : 'grab',
    opacity: 1,
    userSelect: 'none',
    background: isLast
      ? 'white'
      : isDragging
        ? 'var(--primary-200)'
        : undefined,
    zIndex: isDragging ? 999 : isLast ? 3 : undefined,
    willChange: 'transform',
    transformStyle: 'preserve-3d'
  };

  return (
    <TableCell
      ref={setNodeRef}
      {...attributes}
      {...(disabledDrag ? {} : listeners)}
      sx={{
        ...(isLast
          ? {
              position: 'sticky',
              right: 0,
              backgroundColor: 'white',
              zIndex: 3,
              boxShadow: '-2px 0 5px rgba(0,0,0,0.1)'
            }
          : {}),
        ...(column.width
          ? {
              width: column.width,
              maxWidth: column.width,
              minWidth: column.width
            }
          : {})
      }}
      style={style}
    >
      <div className="w-full flex items-center justify-between gap-2 text-nowrap">
        <span>
          {typeof column.label === 'string' ? t(column.label) : column.label}
        </span>
        <div className="flex items-center gap-2">
          {column.showSort && (
            <span
              onPointerDown={(e) => e.stopPropagation()}
              className="inline-flex"
            >
              <TableSortLabel
                active={sort[0] === column.id}
                direction={sort[0] === column.id ? sort[1] : SortOrder.Ascending}
                onClick={() => onSort(column.id)}
                className="always-visible"
              />
            </span>
          )}

          {column.showSearch && (() => {
            const searchKey =
              typeof column.field === 'string' && column.field.includes('|')
                ? column.field
                : column.isTranslated
                  ? `${column.id}.${language}`
                  : column.id;
            return (
              <TableSearchIcon
                columnId={searchKey}
                value={search[searchKey] || ''}
                clearSearch={clearSearch}
                onSearchChange={onSearch}
              />
            );
          })()}

          {column.showFilter && (
            <TableColumnFilter
              columnId={
                column.isTranslated ? `${column.id}.${language}` : column.id
              }
              value={filters[column.id] || []}
              filterData={column.filterData}
              selectionMode={column.selectionMode}
              onFilterChange={onFilter}
              clearFilters={clearFilters}
              showSearchInput={column?.showSearchInput}
              renderFilterItem={column.renderFilterItem}
            />
          )}

          {column.showDateRangeFilter && (
            <TableDateRangeFilter
              columnId={
                column.isTranslated ? `${column.id}.${language}` : column.id
              }
              currentRange={[
                betweenFilters[column.id]?.from
                  ? new Date(betweenFilters[column.id].from)
                  : null,
                betweenFilters[column.id]?.to
                  ? new Date(betweenFilters[column.id].to)
                  : null
              ]}
              clearDateRangeFilters={clearDateRangeFilters}
              onFilterChange={onDateRangeFilter}
            />
          )}
        </div>
      </div>
    </TableCell>
  );
};
